import { OptionType } from '@cognite/cogs.js';
import {
  isConditionWithValueLabel,
  LogicalRule,
  RelationalCondition,
  RelationalRule,
} from 'utils/rules';

const booleanValues: OptionType<string>[] = [
  { value: 'true', label: 'true' },
  { value: 'false', label: 'false' },
];

const booleanGroupValues: OptionType<string>[] = [
  ...booleanValues,
  { value: 'true, false', label: 'true, false' },
];

export const booleanDefaultOption: OptionType<string> = {
  value: 'Select boolean value…',
  label: 'Select boolean value…',
};

const checkIsOneOfCondition = (condition: RelationalCondition) =>
  condition === RelationalCondition.IS_ONE_OF ||
  condition === RelationalCondition.IS_NOT_ONE_OF;

export const getBooleanOptions = (condition: LogicalRule | RelationalRule) => {
  if (condition._type === 'LogicalRule') return [];

  return checkIsOneOfCondition(condition.condition)
    ? booleanGroupValues
    : booleanValues;
};

const checkBooleanAttribute = (condition: RelationalRule) =>
  condition?.fieldType === 'Boolean';

/** Checks if a condition needs an input text for adding values. */
export const needValueInputOptions = (condition: RelationalRule) =>
  isConditionWithValueLabel(condition.condition) &&
  !condition.isPropertyComparison &&
  !checkBooleanAttribute(condition);

/** Checks if a condition needs a drop-down that allows selecting property comparison. */
export const needPropertyComparisonOptions = (condition: RelationalRule) =>
  isConditionWithValueLabel(condition.condition) &&
  condition.isPropertyComparison;

/** Checks if a condition needs a drop-down that allows selecting between true/false options. */
export const needBooleanOptions = (condition: RelationalRule) =>
  isConditionWithValueLabel(condition.condition) &&
  !condition.isPropertyComparison &&
  checkBooleanAttribute(condition);

/** For boolean values, if we change the condition from IS_ONE_OF (with values like "true, false") 
 to simple conditions, then we need to keep just the first single value. */
export const needBooleanCorrection = (
  condition: RelationalRule,
  value?: RelationalCondition
) => {
  if (!value) return false;

  return checkBooleanAttribute(condition) && !checkIsOneOfCondition(value);
};

const isBasicType = (conditionType: string) => {
  const type = conditionType.replace(/\[|\]|!/g, '');

  return (
    type === 'Int' ||
    type === 'Int64' ||
    type === 'Float' ||
    type === 'String' ||
    type === 'Boolean'
  );
};

/** Check if an attribute is a relation.
 * e.g. species, homeworld.name are relations */
export const isRelationalAttribute = (
  condition: RelationalRule | LogicalRule
) => {
  if (condition._type === 'LogicalRule') {
    return false;
  }

  return condition.property.includes('.') || !isBasicType(condition.fieldType);
};

export const getValueDisplayed = (value: string, isManyToMany?: boolean) =>
  isManyToMany ? clearExtraItems(value).split('.') : value.split('.');

const clearExtraItems = (property: string) => property.replaceAll('.items', '');
